import React, { useEffect, useMemo } from 'react';
import { useTable, useSortBy, useExpanded, usePagination } from 'react-table';
import {
  Card,
  Box,
  Flex,
  Button,
  Text,
  Column,
  Select,
} from '../design-system';
import styled from 'styled-components';
import {
  FaCaretDown,
  FaCaretRight,
  FaEdit,
  FaRegTrashAlt,
  FaStickyNote,
  FaRegStickyNote,
} from 'react-icons/fa';
import { get } from 'lodash';
import { utcToLocal, fieldFormat } from '../helpers/utils';
import Loadable from './Loadable';
import SortIndicator, { ariaSort, HeaderContent } from './SortIndicator';

const More = styled(Flex)`
  margin-left: 1rem;
  &:hover {
    cursor: pointer;
  }
`;

const ExpanderButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 0;
  padding: 4px 8px;
  cursor: pointer;
  color: inherit;
`;

const StyledTable = styled.table`
  flex: 1;
  border-spacing: 0;
  border-collapse: collapse;
  width: 100%;
  font-size: 90%;
  tbody > tr:nth-child(odd) {
    background-color: ${(p) => p.theme.colors.greyLightest};
  }
  th {
    font-size: 14px;
    position: sticky;
    top: 0;
    background-color: white;
    /* Headers that wrap to two lines otherwise sit at a different height than
       their single-line neighbours. */
    vertical-align: middle;
  }
  thead > tr > th:first-child,
  tbody > tr > td:first-child {
    padding-left: 2rem;
  }
  thead > tr > th:last-child,
  tbody > tr > td:last-child {
    padding-right: 2rem;
  }
  td,
  th {
    text-align: left;
    padding: 8px;
    max-width: 400px;
    min-width: 100px;
  }
  td {
    height: 56px;
    overflow-wrap: break-word;
  }
`;

interface CollapsibleSubRowProps {
  secondaryColumns: any[];
  actionColumns: any[];
  original: any;
}

const CollapsibleSubRowComponent = ({
  secondaryColumns = [],
  actionColumns = [],
  original = {},
}: CollapsibleSubRowProps) => {
  const safeSecondary = secondaryColumns ?? [];
  const safeActions = actionColumns ?? [];
  const { rows, prepareRow } = useTable({
    columns: [...safeSecondary, ...safeActions],
    data: [{ ...(original ?? {}) }],
  });

  const filteredColumns = safeSecondary.filter(
    (column) => typeof column.accessor === 'string' && !column.accessor.includes('notes'),
  );
  if (!rows?.length) return null;
  const subRow = { ...rows[0] };
  prepareRow(subRow);

  // To generalize accessors
  const notesAccessor = get(
    safeSecondary.find(
      (column) =>
        typeof column.accessor === 'string' &&
        column.accessor.toLowerCase().includes('notes'),
    ),
    'accessor',
  );

  const editAccessor = get(
    safeActions.find(
      (column) =>
        typeof column.accessor === 'string' &&
        column.accessor.toLowerCase().includes('edit'),
    ),
    'accessor',
  );

  const addAccessor = get(
    safeActions.find(
      (column) =>
        typeof column.accessor === 'string' &&
        (column.accessor.toLowerCase().includes('add') ||
          column.accessor.toLowerCase().includes('create')),
    ),
    'accessor',
  );

  // Needed fields
  const notes = subRow.cells.find((cell) => cell.column.id === notesAccessor);
  const editAction = subRow.cells.find(
    (cell) => cell.column.id === editAccessor,
  );
  const addAction = subRow.cells.find((cell) => cell.column.id === addAccessor);

  return (
    <Card id="table-card" pt={4} pb={1} position="relative">
      {editAction && original?.canEdit && (
        <Box position="absolute" right={-4} top={-6}>
          {editAction.render('Cell')}
        </Box>
      )}
      {filteredColumns.length > 0 && (
        <Column>
          <Card mb={1} pt={1} pb={1}>
            <Flex>
              {filteredColumns.map((column) => {
                const details = subRow.cells.find(
                  (cell) => cell.column.id === column.accessor,
                );
                return (
                  <Column key={column.accessor}>
                    <Text fontWeight="bold" fontSize={14} mb={1}>
                      {column.Header}
                    </Text>
                    <Text fontSize={13}>
                      {details ? details.render('Cell') : null}
                    </Text>
                  </Column>
                );
              })}
            </Flex>
          </Card>
        </Column>
      )}
      {notes && <Column>{notes.render('Cell')}</Column>}
      {addAction && (
        <Column>
          <Flex justifyContent="flex-end" alignItems="center" mb={1}>
            {addAction.render('Cell')}
          </Flex>
        </Column>
      )}
    </Card>
  );
};

interface TableProps {
  name?: string;
  onPageChange?: (params: {
    pageIndex: number;
    pageSize: number;
    sortColumn?: string;
    sortDesc?: boolean;
  }) => void;
  columns: any[];
  data: any;
  loading?: boolean;
  pagination?: boolean;
}

export const Table = ({
  name,
  onPageChange,
  columns,
  data,
  loading,
  pagination = true,
}: TableProps) => {
  // Check if data is paginated from server
  const tableData = useMemo(() => {
    if (!data) return [];
    return data.current_page ? data.data || [] : Array.isArray(data) ? data : [];
  }, [data]);

  const isPaginatedByServer = !!(data && data.current_page);

  const {
    current_page: currentPage = 1,
    last_page: tablePageCount = 0,
    per_page: perPage = 50,
  } = useMemo(
    () => (isPaginatedByServer && data ? data : {}),
    [data, isPaginatedByServer],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, sortBy },
  } = useTable(
    {
      manualPagination: isPaginatedByServer,
      pageCount: tablePageCount,
      initialState: {
        pageIndex: currentPage - 1,
        pageSize: perPage,
      },
      columns,
      data: tableData,
      manualSortBy: isPaginatedByServer,
    },
    useSortBy,
    usePagination,
  );

  const [sorting] = sortBy;

  const { id: sortColumn, desc: sortDesc } = sorting || {};

  useEffect(() => {
    if (onPageChange) {
      onPageChange({ pageIndex, pageSize, sortColumn, sortDesc });
    }
  }, [pageIndex, pageSize, sortColumn, sortDesc]);

  const rowData = pagination ? page : rows;

  return (
    <Flex
      flex={1}
      height="100%"
      minHeight="5rem"
      flexDirection="column"
      style={{ overflowX: 'scroll' }}
    >
      <Loadable loading={loading}>
        <StyledTable {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key: hgKey, ...hgProps } =
                headerGroup.getHeaderGroupProps();
              return (
                <tr key={hgKey} {...hgProps}>
                  {headerGroup.headers.map((column) => {
                    const { key: colKey, ...colProps } = column.getHeaderProps(
                      column.getSortByToggleProps(),
                    );
                    return (
                      <th
                        key={colKey}
                        {...colProps}
                        style={{ textAlign: column.alignRight && 'right' }}
                        aria-sort={ariaSort(column)}
                      >
                        <HeaderContent $alignRight={column.alignRight}>
                          {column.render('Header')}
                          <SortIndicator column={column} />
                        </HeaderContent>
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {(rowData?.length ?? 0) > 0 ? (
              rowData.map((row) => {
                prepareRow(row);
                const { key: rowKey, ...rowProps } = row.getRowProps();
                return (
                  <tr key={rowKey} {...rowProps}>
                    {row.cells.map((cell) => {
                      const { key: cellKey, ...cellProps } =
                        cell.getCellProps();
                      return (
                        <td
                          key={cellKey}
                          {...cellProps}
                          style={{
                            textAlign: cell.column.alignRight && 'right',
                          }}
                        >
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  style={{ textAlign: 'center', padding: '2rem' }}
                  colSpan={columns?.length ?? 1}
                >
                  No {name || 'Data'}
                </td>
              </tr>
            )}
          </tbody>
        </StyledTable>
        {pagination && pageCount > 1 && (
          <Flex
            justifyContent="center"
            alignItems="center"
            mt={4}
            py={3}
            style={{ borderTop: '1px solid lightGrey' }}
          >
            <Box mr={3}>
              <span>
                Page{' '}
                <strong>
                  {pageIndex + 1} of {pageCount || 1}
                </strong>{' '}
              </span>
            </Box>
            <Box mr={3}>
              <Button.Group>
                <Button
                  size="sm"
                  leftIcon="page-first"
                  onClick={() => gotoPage(0)}
                  disabled={!canPreviousPage}
                />
                <Button
                  size="sm"
                  leftIcon="chevron-left"
                  onClick={() => previousPage(0)}
                  disabled={!canPreviousPage}
                />
                <Button
                  size="sm"
                  leftIcon="chevron-right"
                  onClick={() => nextPage()}
                  disabled={!canNextPage}
                />
                <Button
                  size="sm"
                  leftIcon="page-last"
                  onClick={() => gotoPage(pageCount - 1)}
                  disabled={!canNextPage}
                />
              </Button.Group>
            </Box>
            <Flex alignItems="center">
              <Box mr={2}>Show</Box>
              <Box>
                <Select
                  size="sm"
                  style={{ width: 70 }}
                  name="pageSize"
                  options={[10, 20, 30, 40, 50].map((size) => ({
                    label: size,
                    value: size,
                  }))}
                  value={pageSize.toString()}
                  onChange={(_, value) => {
                    gotoPage(0);
                    setPageSize(Number(value));
                  }}
                />
              </Box>
            </Flex>
          </Flex>
        )}
      </Loadable>
    </Flex>
  );
};

interface CollapsibleRowTableProps {
  name?: string;
  columns: {
    primary?: any[];
    secondary?: any[];
    actions?: any[];
  };
  data: any[];
}

export const CollapsibleRowTable = ({
  name,
  columns: { primary = [], secondary = [], actions = [] },
  data = [],
}: CollapsibleRowTableProps) => {
  // react-table v7's auto-reset effects dispatch whenever the columns array
  // identity changes; an inline array rebuilt on every render makes each
  // dispatch trigger the next one — an infinite update loop.
  const columns = React.useMemo(
    () => [
      ...primary,
      {
        Header: '',
        id: 'expander',
        Cell: ({ row }: { row: any }) => {
          const toggleProps =
            typeof row.getToggleRowExpandedProps === 'function'
              ? row.getToggleRowExpandedProps()
              : typeof row.getExpandedToggleProps === 'function'
                ? row.getExpandedToggleProps()
                : {};
          return (
            <Flex justifyContent="flex-end">
              <ExpanderButton
                type="button"
                id="expand-button"
                aria-label={row.isExpanded ? 'Collapse row' : 'Expand row'}
                aria-expanded={row.isExpanded}
                onClick={(e) => {
                  e.stopPropagation();
                  if (typeof toggleProps.onClick === 'function') {
                    toggleProps.onClick(e);
                  } else if (typeof row.toggleRowExpanded === 'function') {
                    row.toggleRowExpanded();
                  }
                }}
              >
                {row.isExpanded ? <FaCaretDown /> : <FaCaretRight />}
              </ExpanderButton>
            </Flex>
          );
        },
      },
    ],
    [primary],
  );

  const safeData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const table = useTable(
    {
      columns,
      data: safeData,
    },
    useSortBy,
    useExpanded,
  );
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow } =
    table as any;
  const rows = (table as any).rows ?? [];
  const flatColumns = (table as any).flatColumns ?? [];
  return (
    <StyledTable {...getTableProps()}>
      <thead>
        {headerGroups.map((headerGroup) => {
          const { key: hgKey, ...hgProps } = headerGroup.getHeaderGroupProps();
          return (
            <tr key={hgKey} {...hgProps}>
              {headerGroup.headers.map((column) => {
                const { key: colKey, ...colProps } = column.getHeaderProps(
                  column.getSortByToggleProps(),
                );
                return (
                  <th key={colKey} {...colProps} aria-sort={ariaSort(column)}>
                    <HeaderContent $alignRight={column.alignRight}>
                      {column.render('Header')}
                      <SortIndicator column={column} />
                    </HeaderContent>
                  </th>
                );
              })}
            </tr>
          );
        })}
      </thead>
      <tbody {...getTableBodyProps()}>
        {(rows?.length ?? 0) > 0 ? (
          rows.map((row) => {
            prepareRow(row);
            const { key: rowKey } = row.getRowProps();
            return (
              <React.Fragment key={rowKey}>
                <tr id={name}>
                  {row.cells.map((cell) => {
                    const { key: cellKey, ...cellProps } = cell.getCellProps();
                    return (
                      <td key={cellKey} {...cellProps}>
                        {cell.render('Cell')}
                      </td>
                    );
                  })}
                </tr>
                {row.isExpanded && (
                  <tr>
                    <td colSpan={flatColumns?.length ?? 1}>
                      <CollapsibleSubRowComponent
                        secondaryColumns={secondary}
                        actionColumns={actions}
                        original={row.original}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })
        ) : (
          <tr>
            <td
              style={{ textAlign: 'center', padding: '2rem' }}
              colSpan={flatColumns?.length ?? 1}
            >
              No {name || 'Data'}
            </td>
          </tr>
        )}
      </tbody>
    </StyledTable>
  );
};

interface TableCardHeaderProps {
  name?: string;
  action?: string;
  onClick?: () => void;
  disabled?: boolean;
  updatedAt?: string;
}

export const TableCardHeader = ({
  name,
  action,
  onClick,
  disabled,
  updatedAt,
}: TableCardHeaderProps) => (
  <Card.Header>
    <Flex justifyContent="flex-start" alignItems="center">
      <Text fontWeight={700} fontSize={20} lineHeight="20px" mr={3}>
        {name}
      </Text>
      {updatedAt && (
        <Text
          fontWeight={500}
          fontSize={14}
          lineHeight="14px"
          color="grey"
          alignSelf="flex-end"
        >
          Updated: {utcToLocal(updatedAt).fromNow()}
        </Text>
      )}
      {!disabled && (
        <Box justifySelf="flex-end" ml="auto">
          <Button
            id={fieldFormat(name, '-button-1')}
            variant="primaryText"
            type="button"
            onClick={onClick}
            leftIcon="plus"
            leftIconProps={{ size: 20 }}
          >
            {action}
          </Button>
        </Box>
      )}
    </Flex>
  </Card.Header>
);

export const TableBody = styled(Card.Body)`
  overflow-x: auto;
  padding: 0;
`;
